/**
 * "Cadastrar no IW", dentro do bloco do vínculo (NR-258), no Caso marcado como novo no IW.
 *
 * Criar o paciente no IW é saída externa: sai só com a confirmação de uma pessoa (restrição 9). A confirmação mostra
 * o cadastro como vai ao IW, já preenchido com o que o e-mail trouxe, e a pessoa corrige ou completa nascimento, CPF e
 * filial. O nome vem do e-mail e o apelido é o sugerido a partir dele: os dois não se editam aqui (o apelido trocado
 * teria de ir para a fila, que só guarda códigos). Cada desfecho é dito em texto — o IW criou e o
 * Caso está vinculado; o IW não respondeu e o cadastro ficou na fila; o IW já tem paciente com esses dados (o bloco
 * passa a mostrar os candidatos); ou o IW recusou, com o campo e o motivo.
 */

import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../shared/api/erros';
import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import {
  ROTULOS_DO_CAMPO_DO_IW,
  cadastrarPacienteNoIw,
  opcoesDoCadastroPrevisto,
  type DadosDoCadastro,
  type ResultadoDoCadastroNoIw,
} from './api/cadastroNoIw';
import { chavesDeCasos } from './api/casos';
import type { CasoDetalhado } from './api/tipos';

/** Mensagem de cada erro, por `codigo`/`status` — mesma disciplina de `VinculoComOIw.tsx`. */
function mensagemDeErroDoCadastro(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    switch (causa.codigo) {
      case 'IW_PATIENT_REGISTRATION_DISABLED':
        return 'O cadastro de paciente no IW ainda não está ligado neste ambiente. Cadastre no IW e vincule pela busca.';
      case 'IW_PATIENT_REGISTRATION_INCOMPLETE':
      case 'IW_PATIENT_REGISTRATION_INVALID':
      case 'VALIDATION_ERROR':
      case 'IW_PATIENT_ALREADY_REGISTERED':
      case 'IW_PATIENT_REGISTRATION_ALREADY_PENDING':
      case 'IW_OPERATION_CASE_CLOSED':
        // O texto vem do catálogo do back e já diz o que corrigir, ou o caminho a seguir (a busca, no já cadastrado).
        return causa.message;
      case 'IW_PATIENT_NOT_NEW':
        return 'O vínculo deste Caso mudou e ele não está mais como novo no IW.';
      case 'CASE_ALREADY_LINKED_TO_IW':
        return 'O Caso já está vinculado a um paciente do IW.';
    }
    if (causa.status === 404) return 'O caso não foi encontrado para o seu núcleo.';
  }
  return 'Não foi possível cadastrar no IW. Tente novamente.';
}

/** O texto do desfecho. Fica com o bloco do vínculo: o desfecho muda o vínculo, e este botão pode sair da tela. */
function mensagemDoDesfecho(resultado: ResultadoDoCadastroNoIw): string {
  switch (resultado.desfecho) {
    case 'Linked':
      return 'Paciente cadastrado no IW e vinculado ao Caso.';
    case 'Pending':
      return 'O IW não respondeu agora. O cadastro ficou na fila e é refeito sozinho, sem criar o paciente em dobro.';
    case 'Homonym':
      return 'O IW já tem paciente com estes dados e não criou outro. Veja os candidatos e escolha o paciente certo.';
    case 'Refused': {
      const campos = resultado.camposRecusados
        .map((c) => `${ROTULOS_DO_CAMPO_DO_IW[c.campo] ?? c.campo}: ${c.mensagem}`)
        .join('; ');
      const detalhe = [campos, resultado.motivo].filter((parte) => parte !== null && parte !== '').join(' ');
      // A recusa da plataforma não é do IW: atribuí-la ao IW manda a pessoa procurar o problema no lugar errado.
      const quem = resultado.recusadoPelaPlataforma ? 'O cadastro não foi enviado ao IW.' : 'O IW recusou o cadastro.';
      return detalhe === '' ? quem : `${quem} ${detalhe}`;
    }
    case 'Unanswered':
      return 'O IW não respondeu em nenhuma tentativa, e nada foi confirmado. Tente de novo quando ele voltar.';
    default:
      return 'O IW respondeu de um jeito que esta tela ainda não conhece. Recarregue o Caso para ver o estado atual.';
  }
}

/** Erros que não mudam o vínculo: ficam ao lado do botão, como alerta, até a próxima tentativa. */
const ERROS_QUE_FICAM_NO_BOTAO: readonly string[] = [
  'IW_PATIENT_REGISTRATION_DISABLED',
  'IW_PATIENT_REGISTRATION_INCOMPLETE',
  'IW_PATIENT_REGISTRATION_INVALID',
  'IW_PATIENT_REGISTRATION_ALREADY_PENDING',
];

function ficaNoBotao(causa: unknown): boolean {
  if (!(causa instanceof ErroDaApi) || causa.status !== 409) return true;
  return causa.codigo !== null && ERROS_QUE_FICAM_NO_BOTAO.includes(causa.codigo);
}

const CPF_COMPLETO = /^\d{11}$/;

export function CadastroNoIw({
  caso,
  aoAtualizar,
  aoAvisar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
  /** O desfecho, em texto, para quem continua na tela depois que o vínculo muda (vinculado, homônimo). */
  aoAvisar: (texto: string) => void;
}): React.JSX.Element {
  const [confirmando, definirConfirmando] = useState(false);
  const clienteDeConsultas = useQueryClient();
  const previsto = useQuery(opcoesDoCadastroPrevisto(caso.id, confirmando));
  // `null` = ainda não mexeu: vale o que veio do Caso. O que a pessoa digita passa por cima.
  const [nascimento, definirNascimento] = useState<string | null>(null);
  const [cpf, definirCpf] = useState<string | null>(null);
  const [filial, definirFilial] = useState<number | null>(null);

  const dados: DadosDoCadastro = {
    nascimento: nascimento ?? previsto.data?.nascimento ?? '',
    cpf: cpf ?? previsto.data?.cpf ?? '',
    filial: filial ?? previsto.data?.filial ?? null,
  };
  // Fechar o formulário (cancelar ou confirmar) esquece o que foi digitado: ao reabrir, a prévia vem de novo do Caso, e
  // um valor velho não pode voltar como "correção humana" por cima de um mais novo (revisão do PR #85).
  const esquecerDigitado = (): void => {
    definirNascimento(null);
    definirCpf(null);
    definirFilial(null);
  };
  const cpfDigitos = dados.cpf.replace(/\D/g, '');
  const cpfValido = cpfDigitos === '' || CPF_COMPLETO.test(cpfDigitos);
  const prontoParaEnviar =
    previsto.isSuccess && dados.nascimento !== '' && cpfValido && dados.filial !== null;

  const cadastrar = useMutation({
    mutationFn: (enviados: DadosDoCadastro) => cadastrarPacienteNoIw(caso.id, enviados),
    onSuccess: (resultado) => {
      aoAvisar(mensagemDoDesfecho(resultado));
      aoAtualizar(resultado.caso);
    },
    // 409 que muda o vínculo (o Caso mudou, encerrou ou já foi cadastrado): recarrega o Caso, como o bloco do vínculo
    // faz, e a mensagem vai para o aviso do bloco, que sobrevive ao botão sair da tela.
    onError: (erro) => {
      if (ficaNoBotao(erro)) return;
      aoAvisar(mensagemDeErroDoCadastro(erro));
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.detalhe(caso.id) });
    },
  });
  const erroNaTela = cadastrar.isError && ficaNoBotao(cadastrar.error);

  return (
    <div className="space-y-2">
      <Button
        variant="outlined"
        disabled={cadastrar.isPending}
        onClick={() => {
          definirConfirmando(true);
        }}
      >
        Cadastrar no IW
      </Button>

      {erroNaTela ? (
        <p role="alert" className="text-[0.8125rem] font-semibold text-[var(--roxo)]">
          {mensagemDeErroDoCadastro(cadastrar.error)}
        </p>
      ) : null}

      <ConfirmacaoHumana
        aberto={confirmando}
        titulo="Cadastrar o paciente no IW?"
        descricao="Confira o cadastro como vai ao IW. O que veio no e-mail já está preenchido; corrija ou complete antes de cadastrar. Se o IW já tiver alguém com esses dados, nada é criado e você escolhe entre os candidatos."
        rotuloDeConfirmar="Cadastrar"
        confirmarDesabilitado={!prontoParaEnviar}
        aoCancelar={() => {
          definirConfirmando(false);
          esquecerDigitado();
        }}
        aoConfirmar={() => {
          if (!prontoParaEnviar) return;
          definirConfirmando(false);
          cadastrar.reset();
          aoAvisar('');
          cadastrar.mutate(dados);
          esquecerDigitado();
        }}
      >
        {previsto.isError ? (
          <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
            Não foi possível montar o cadastro deste Caso agora. Feche e tente de novo.
          </p>
        ) : null}
        <TextField
          size="small"
          label="Nome"
          value={previsto.data?.nome ?? ''}
          helperText="Vem do e-mail; não se edita aqui."
          slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          label="Apelido"
          value={previsto.data?.apelido ?? ''}
          helperText="O primeiro nome; no IW dá para trocar depois."
          slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          size="small"
          required
          label="Data de nascimento"
          value={dados.nascimento}
          onChange={(evento) => {
            definirNascimento(evento.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          label="CPF"
          value={dados.cpf}
          onChange={(evento) => {
            definirCpf(evento.target.value);
          }}
          error={!cpfValido}
          helperText={cpfValido ? 'Opcional no IW.' : 'O CPF tem 11 dígitos.'}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          select
          size="small"
          required
          label="Filial do IW"
          value={dados.filial ?? ''}
          onChange={(evento) => {
            definirFilial(Number(evento.target.value));
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        >
          {(previsto.data?.filiais ?? []).map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.nome}
            </MenuItem>
          ))}
        </TextField>
      </ConfirmacaoHumana>
    </div>
  );
}
